/**
 * HALO - Export Data Page
 * Admin page for exporting organization data
 */

import { useState } from "react";
import { motion } from "framer-motion";
import {
  Download,
  FileJson,
  FileSpreadsheet,
  Database,
  Users,
  AlertTriangle,
  Clock,
  CheckCircle,
  Loader2,
} from "lucide-react";
import {
  exportOrganizationData,
  downloadExportData,
  ExportProgress,
} from "@/lib/supabase";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";

interface ExportDataPageProps {
  organizationId: string;
  userRole: "admin" | "manager" | "employee";
}

type ExportFormat = "json" | "csv";

export function ExportDataPage({
  organizationId,
  userRole,
}: ExportDataPageProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastExport, setLastExport] = useState<{
    date: string;
    format: string;
  } | null>(null);

  const handleExport = async (format: ExportFormat) => {
    if (userRole !== "admin") {
      setError("Only administrators can export organization data");
      return;
    }

    setIsExporting(true);
    setError(null);
    setProgress({ stage: "users", progress: 0, total: 100 });

    try {
      const data = await exportOrganizationData(organizationId, setProgress);

      if (data) {
        downloadExportData(data, format);
        setLastExport({ date: new Date().toISOString(), format });
      } else {
        setError("Failed to export data. Please try again.");
      }
    } catch (err) {
      console.error("[Export] Error:", err);
      setError("An error occurred while exporting data.");
    } finally {
      setIsExporting(false);
      setProgress(null);
    }
  };

  const getProgressLabel = () => {
    if (!progress) return "";
    switch (progress.stage) {
      case "users":
        return "Fetching users...";
      case "logs":
        return "Fetching wellness logs...";
      case "alerts":
        return "Fetching alerts...";
      case "complete":
        return "Preparing download...";
      default:
        return "";
    }
  };

  if (userRole !== "admin") {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
            <div>
              <h3 className="font-medium text-yellow-500">
                Admin Access Required
              </h3>
              <p className="text-sm text-muted-slate mt-1">
                Only administrators can export organization data.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col gap-4 py-4 md:gap-6 md:py-6"
    >
      <div className="px-4 lg:px-6">
        <h1 className="text-2xl font-bold text-foreground mb-1">
          Export Organization Data
        </h1>
        <p className="text-muted-slate">
          Download a complete backup of your organization's data.
        </p>
      </div>

      {/* Included Data Cards */}
      <div className="grid grid-cols-1 gap-4 px-4 sm:grid-cols-3 lg:px-6">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Database className="w-4 h-4 text-primary" />
              Organization
            </CardDescription>
            <CardTitle className="text-lg">Included</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-slate">
              Full organization settings and configuration
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              Users
            </CardDescription>
            <CardTitle className="text-lg">Included</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-slate">
              All employee profiles and metadata
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-primary" />
              Alerts
            </CardDescription>
            <CardTitle className="text-lg">Included</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-slate">
              History of all wellness alerts
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="px-4 lg:px-6">
        <Card>
          <CardHeader>
            <CardTitle>Select Export Format</CardTitle>
            <CardDescription>
              Choose the format that best suits your needs
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <p className="text-sm text-red-500">{error}</p>
              </div>
            )}

            {lastExport && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-500">
                    Last exported: {new Date(lastExport.date).toLocaleString()}{" "}
                    ({lastExport.format.toUpperCase()})
                  </span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <button
                onClick={() => handleExport("json")}
                disabled={isExporting}
                className="halo-card flex items-center gap-4 text-left w-full hover:border-blue-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <FileJson className="w-6 h-6 text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground">JSON Format</h3>
                  <p className="text-sm text-muted-slate truncate">
                    Complete data structure
                  </p>
                </div>
                <Download className="w-5 h-5 text-muted-slate group-hover:text-blue-500 transition-colors" />
              </button>

              <button
                onClick={() => handleExport("csv")}
                disabled={isExporting}
                className="halo-card flex items-center gap-4 text-left w-full hover:border-green-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <FileSpreadsheet className="w-6 h-6 text-green-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground">CSV Format</h3>
                  <p className="text-sm text-muted-slate truncate">
                    Spreadsheet-compatible
                  </p>
                </div>
                <Download className="w-5 h-5 text-muted-slate group-hover:text-green-500 transition-colors" />
              </button>
            </div>

            {isExporting && progress && (
              <div className="p-4 bg-muted/50 rounded-xl border border-border">
                <div className="flex items-center gap-3 mb-3">
                  <Loader2 className="w-4 h-4 text-primary animate-spin" />
                  <span className="text-sm font-medium text-foreground">
                    {getProgressLabel()}
                  </span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-primary rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress.progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <p className="text-xs text-muted-slate mt-2 text-right">
                  {progress.progress}%
                </p>
              </div>
            )}
          </CardContent>

          <CardFooter className="bg-muted/20 mt-2 p-4">
            <div className="flex items-start gap-2.5">
              <Clock className="w-4 h-4 text-muted-slate mt-0.5" />
              <div>
                <p className="text-xs text-muted-slate">
                  Large datasets may take a few minutes to export. The file will
                  download automatically when ready.
                </p>
              </div>
            </div>
          </CardFooter>
        </Card>
      </div>
    </motion.div>
  );
}
